import {
  AspectRatio,
  Box,
  HStack,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import StepTitle from "./Title";

/**
 * @typedef StepperStep
 * @property {string} [alias]
 * @property {string} title
 * @property {() => void} [isEnabled]
 */

/**
 * @typedef Props
 * @property {number} stepIndex
 * @property {(index: number) => void} setStepIndex
 * @property {StepperStep[]} steps
 * @property {number} [scrollMarginTop]
 *
 * @param {import("react").PropsWithChildren<Props>} props
 */
function Stepper({
  stepIndex,
  setStepIndex,
  steps = [],
  scrollMarginTop,
  children,
}) {
  const backgroundColorCard = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const backgroundColor = useColorModeValue("gray.300", "gray.600");
  const backgroundColorActive = useColorModeValue(
    "brandSecondary.300",
    "brandSecondary.600",
  );

  const isSmallLayout = useBreakpointValue({ base: true, sm: false });

  /** @type {import("react").MutableRefObject<HTMLDivElement | null>} */
  const ref = useRef(null);

  const isMounted$ = useRef(false);

  // focus step box when stepIndex changes
  useEffect(() => {
    if (!isMounted$.current) {
      isMounted$.current = true;
    } else {
      ref.current?.scrollIntoView({ inline: "start", behavior: "smooth" });
    }
  }, [stepIndex]);

  return (
    <Stack spacing="1rem">
      {steps?.map?.((step, index) => (
        <Box
          pt={
            !isSmallLayout && stepIndex !== index && index === 0
              ? "1rem"
              : undefined
          }
          position="relative"
          key={index}>
          <Stack
            direction={{ base: "column", sm: "row" }}
            align="flex-start"
            spacing="1rem"
            _before={
              index < steps.length - 1
                ? {
                    content: `""`,
                    position: "absolute",
                    top: "16px",
                    left: "14px",
                    bottom: "-32px",
                    width: "2px",
                    backgroundColor:
                      stepIndex > index
                        ? backgroundColorActive
                        : backgroundColor,
                  }
                : undefined
            }>
            <HStack flexShrink={0}>
              <AspectRatio ratio={1} width="30px">
                <Text
                  fontSize="1.25rem"
                  fontWeight="bold"
                  backgroundColor={
                    stepIndex >= index ? backgroundColorActive : backgroundColor
                  }
                  textAlign="center"
                  w="1.25rem"
                  borderRadius="full"
                  mt={
                    stepIndex === index && !isSmallLayout ? "1rem" : undefined
                  }>
                  {index + 1}
                </Text>
              </AspectRatio>

              <Box
                display={{
                  base: stepIndex === index ? "none" : "block",
                  sm: "none",
                }}>
                <StepTitle
                  step={step}
                  index={index}
                  setStepIndex={setStepIndex}
                />
              </Box>
            </HStack>

            {stepIndex === index ? (
              <Box w="full" flexGrow={1}>
                <Box
                  ref={ref}
                  scrollMarginTop={`${(scrollMarginTop ?? 0) + 16}px`}
                  position="relative"
                  backgroundColor={backgroundColorCard}
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={borderColor}
                  borderRadius="1rem">
                  {children}
                </Box>
              </Box>
            ) : (
              <>
                {!isSmallLayout && (
                  <Box flexGrow={1}>
                    <Box display={{ base: "none", sm: "block" }}>
                      <StepTitle
                        step={step}
                        index={index}
                        setStepIndex={setStepIndex}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export default Stepper;
